/**
 * Action: Case management statement (file and serve the CM-110 before a case management
 * conference). Statewide base with the court layer.
 *
 * Every rule cited here comes from the files in data/. Court specifics come from
 * src/courts/ through courtDetail(); anything the files do not cover is listed under
 * "verify" rather than asserted.
 */
import { courtDetail } from "../lib/courtlayer.js";
import {
  BACKWARD_NOTE, checklist, fmt, fmtISO, iso, lastDayBefore, opts, parse, todayISO,
} from "../lib/civil.js";

export const id = "case-management-statement";
export const statewide = true;
// The statewide forms its checklist can link, which a case offers when this action is added to it.
export const forms = ["ca-cm-110", "ca-ra-010"];

const CASE_TXT = {
  unlimited: "Unlimited civil",
  limited: "Limited civil (not a collections case)",
  collections: "A collections case under CRC 3.740",
};
const WHICH_TXT = {
  initial: "The initial case management conference",
  later: "A later (additional) case management conference",
};
const yesNo = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];
const yn = (v) => (v === "yes" ? "Yes" : v === "no" ? "No" : "");

const isInitial = (a) => a.which === "initial";
// CRC 3.740(c)(2): a collections case is exempt from rules 3.721-3.730 until a defendant files a responsive pleading.
const exemptCollections = (a) => a.ctype === "collections" && a.responsive === "no";
// What kind of case it is, kept once per case (caseFacts in src/cases.js).
export const caseFacts = {
  from: (f) => ({
    ctype: f.collections === "yes" ? "collections" : f.limited === "no" ? "unlimited" : f.limited === "yes" && f.collections === "no" ? "limited" : undefined,
  }),
  to: (a) => ({
    limited: { unlimited: "no", limited: "yes" }[a.ctype],
    collections: a.ctype ? (a.ctype === "collections" ? "yes" : "no") : undefined,
  }),
};

/* ------------------------------------------------------------------ steps */
export const steps = [
  {
    id: "conference",
    title: "The conference",
    intro: "The statement is due a set number of days before the conference, so the dates are counted back from it. The wizard doesn't ask for names or case details.",
    fields: [
      { k: "ctype", type: "radio", label: "What kind of case is it?", req: true,
        opts: [
          { v: "unlimited", l: CASE_TXT.unlimited },
          { v: "limited", l: CASE_TXT.limited },
          { v: "collections", l: CASE_TXT.collections, h: "$35,000 or less owed on credit, with no tort or punitive damages, property recovery or prejudgment attachment sought (CRC 3.740(a))." },
        ] },
      { k: "responsive", type: "radio", label: "Has a defendant filed a responsive pleading?", req: true,
        show: (a) => a.ctype === "collections",
        hint: "A collections case is exempt from the case management rules until one is filed (CRC 3.740(c)(2)).",
        opts: yesNo },
      { k: "which", type: "radio", label: "Which conference is the statement for?", req: true, opts: opts(WHICH_TXT) },
      { k: "cmcDate", type: "date", label: "Date of the conference", req: true,
        hint: (a, court) => `The date on the notice or order setting the conference. Deadlines are counted on the calendar of ${court.name}.` },
    ],
  },
  {
    id: "statement",
    title: "The statement",
    intro: "Who files it and what is paid with it.",
    fields: [
      { k: "joint", type: "radio", label: "Will you file a joint statement with one or more other parties?", req: true,
        hint: "Any two or more parties may file one joint statement instead of separate ones (CRC 3.725(b)).",
        opts: yesNo },
      { k: "jury", type: "radio", label: "Does your side want a jury, with the jury fee not yet paid by anyone on your side?", req: true,
        show: isInitial,
        hint: "The $150 jury fee is due on or before the date of the initial case management conference (CCP 631(b)-(c)).",
        opts: yesNo },
    ],
  },
  {
    id: "requests",
    title: "Requests and appearance",
    intro: "These add the papers, fees and notices that go with each request.",
    fields: [
      { k: "continuance", type: "radio", label: "Will you ask the court to continue the conference?", req: true, opts: yesNo },
      { k: "adr", type: "radio", label: "Have all parties agreed to use an ADR process?", req: true,
        hint: "If they have, they must jointly complete and file the court's ADR stipulation form (CRC 3.726).",
        opts: yesNo },
      { k: "appear", type: "radio", label: "How will you appear at the conference?", req: true,
        hint: (a, court) => {
          const d = courtDetail(court, "rules.cmcRemote", "");
          return d.known ? d.text : "Counsel and self-represented parties appear in person or remotely under CRC 3.672 (CRC 3.722(c)).";
        },
        opts: [{ v: "person", l: "In person" }, { v: "remote", l: "Remotely" }] },
    ],
  },
];

/** Shown on the review step. */
export function summary(a, court) {
  const rows = [];
  const add = (l, v) => v && rows.push([l, v]);
  add("Court", court.name);
  add("Case", CASE_TXT[a.ctype]);
  if (a.ctype === "collections") add("Responsive pleading filed", yn(a.responsive));
  add("Conference", WHICH_TXT[a.which]);
  add("Conference date", fmtISO(a.cmcDate));
  add("Joint statement", yn(a.joint));
  if (isInitial(a)) add("Jury wanted, fee unpaid", yn(a.jury));
  add("Ask for a continuance", yn(a.continuance));
  add("All parties agree to ADR", yn(a.adr));
  add("Appearance", a.appear === "remote" ? "Remotely" : a.appear === "person" ? "In person" : "");
  return rows;
}

/* ------------------------------------------------------------------ checklist */
export function build(a, court) {
  const c = checklist();
  const confISO = a.cmcDate || todayISO();
  const conf = parse(confISO);
  const initial = isInitial(a);
  const today = todayISO();
  const cal = court.calendar;

  const fileBy = lastDayBefore(court, confISO, 15);
  const meetBy = initial ? lastDayBefore(court, confISO, 30) : null;
  const noticeBy = initial ? lastDayBefore(court, confISO, 45) : null;
  const lodgeDetail = courtDetail(court, "rules.cmStatement", "local rules on case management statements");
  const lodgeDays = lodgeDetail.known && lodgeDetail.value && lodgeDetail.value.lodgeDaysBefore;
  const lodgeBy = lodgeDays ? lastDayBefore(court, confISO, lodgeDays) : null;
  const remoteBy = a.appear === "remote" ? cal.addCourtDays(conf, -2) : null;
  const moved = [fileBy, meetBy, noticeBy, lodgeBy].some((d) => d && d.moved);
  const when = (d) => fmt(d.safe) + (d.moved ? ` (the count lands on ${fmt(d.date)}, when the court is closed)` : "");

  /* ---- flags ---- */
  if (!cal.isCourtDay(conf)) c.flag("bad", "Check the conference date.", `${fmt(conf)} is not a court day on the calendar of ${court.name}.`);
  if (iso(fileBy.safe) < today) c.flag("warn", "The statement is late.", `It was due ${fmt(fileBy.safe)}, 15 days before the conference (CRC 3.725(a)). File and serve it as soon as possible.`);
  if (exemptCollections(a)) c.flag("info", "Collections case.", "Until a defendant files a responsive pleading, a collections case is exempt from the case management rules in CRC 3.721-3.730 (CRC 3.740(c)(2)). This checklist assumes the court has set a conference anyway; where it does, CRC 3.720(c) applies the case management rules.");

  /* ---- before ---- */
  const p1 = c.section("before", "Phase 1. Before the statement");
  if (initial) {
    c.item(p1, "notice", `Notice of the initial conference must be given to all parties no later than 45 days before it, by ${when(noticeBy)}, unless the court orders otherwise.`, "CRC 3.722(b)");
    c.court(p1, "noticelocal", court, "rules.cmcNotice", "local rules on who gives notice of the initial case management conference, and how", null);
    c.item(p1, "meet", `Meet and confer with all parties, in person or by telephone, no later than ${when(meetBy)}: 30 calendar days before the conference. Cover the subjects in CRC 3.727 plus discovery disputes and schedule, anticipated motions, uncontested and disputed facts and issues, narrowing the issues, settlement, trial availability, and electronically stored information.`, "CRC 3.724");
  } else {
    c.item(p1, "later", "An additional conference can be ordered by the court or requested by a party. The meet-and-confer deadline in CRC 3.724 is tied to the initial conference; check the order setting this one for anything it requires beforehand.", "CRC 3.723; 3.724");
  }
  c.item(p1, "subjects", "Be ready to take a position on the subjects the court considers at the conference, including ADR, trial setting, jury or nonjury trial, and discovery.", "CRC 3.722(c); 3.727");

  /* ---- statement ---- */
  const p2 = c.section("prep", "Phase 2. Prepare the statement");
  c.item(p2, "cm110", "Use the mandatory Case Management Statement (CM-110) and complete every applicable item.", "CRC 3.725(c)", "", ["ca-cm-110"]);
  if (a.joint === "yes") c.item(p2, "joint", "Prepare one joint CM-110 for the parties joining it. Any party that does not join must file and serve its own.", "CRC 3.725(a)-(b)", "", ["ca-cm-110"]);
  else c.item(p2, "own", "Each party files and serves its own statement.", "CRC 3.725(a)", "", ["ca-cm-110"]);
  c.item(p2, "fee", "No filing fee for a case management statement.", "GC 70617(b)(3); Fee Schedule item 61");
  if (a.continuance === "yes") {
    c.court(p2, "cont", court, "rules.cmcContinuance", "local rules on how to ask to continue a case management conference",
      { text: "Ask the court to continue the conference. The downloaded rules don't set a statewide procedure for it.", cite: "CRC 3.723" });
    c.item(p2, "contfee", "Fee for a request, application or motion for (or notice of) a continuance of a case management conference: $20, charged once per continuance, and not charged if the court requires the continuance.", "GC 70617(c)(1); Fee Schedule item 57");
    c.item(p2, "contdates", "Until the court continues the conference, the statement is due 15 days before the date currently set.", "CRC 3.725(a)");
  }
  if (a.adr === "yes") {
    c.court(p2, "adr", court, "rules.adrStipulation", "ADR stipulation form and where to get it",
      { text: "All parties jointly complete the ADR stipulation form in the court's ADR information package, and file it.", cite: "CRC 3.726; 3.221(a)(4)" });
    if (courtDetail(court, "rules.adrStipulation", "").known) c.item(p2, "adrcrc", "The stipulation is completed jointly by all parties and filed with the court.", "CRC 3.726");
    c.verify("Whether the court charges the ADR stipulation as a stipulation and order ($20, GC 70617(c)(2)) or as a stipulation that does not require an order (no fee, GC 70617(b)(5)).");
  }

  /* ---- file and serve ---- */
  const p3 = c.section("file", "Phase 3. File and serve it");
  c.item(p3, "due", `File the statement and serve it on all other parties no later than ${when(fileBy)}: 15 calendar days before the conference.`, "CRC 3.725(a)", "", ["ca-cm-110"]);
  const cms = c.court(p3, "local", court, "rules.cmStatement", "local rules on filing, lodging and courtesy copies of case management statements", null);
  if (cms.known && lodgeBy) c.item(p3, "lodge", `To make an order without an appearance possible, file, serve and lodge it by ${when(lodgeBy)}: ${lodgeDays} days before the conference.`, cms.cite);
  c.court(p3, "efile", court, "efiling.represented", "local rules on whether represented parties must e-file",
    { text: "File the statement with the clerk.", cite: "CRC 3.725(a)" });

  /* ---- fees ---- */
  if (initial && a.jury === "yes") {
    const p4 = c.section("jury", "Phase 4. Jury fee");
    c.item(p4, "fee", `Pay the $150 nonrefundable jury fee on or before ${fmt(conf)}, the date of the initial case management conference. One party demanding a jury on each side pays it.`, "CCP 631(b)-(c); Fee Schedule item 65",
      "If the party first appears after the initial conference, or more than 365 days after the complaint was filed, the fee is instead due 25 days before the date first set for trial (CCP 631(c)(4)).");
    c.item(p4, "waiver", "Not paying it on time waives a jury, unless another party on the same side has paid it.", "CCP 631(f)(5)");
  }

  /* ---- conference ---- */
  const p5 = c.section("conf", "Phase 5. The conference");
  c.court(p5, "dept", court, "departments.caseManagement", "local rules for the department that hears case management conferences", null);
  c.item(p5, "noappear", "The court may review the statements, issue a case management order and tell the parties no appearance is needed. Check for one before the conference.", "CRC 3.722(d)");
  if (a.appear === "remote") {
    c.item(p5, "ra010", `Give notice of intent to appear remotely no later than ${fmt(remoteBy)}, 2 court days before the conference: file a Notice of Remote Appearance (RA-010) with the court, and notify the other parties in writing, electronically or orally.`, "CRC 3.672(g)(2)(A)", "Not needed if you have already given notice to appear remotely for the whole case, or all parties have stipulated to waive notice (CRC 3.672(f), (g)(1)(C)).", ["ca-ra-010"]);
    c.court(p5, "remote", court, "rules.cmcRemote", "local procedures for appearing remotely at a case management conference (CRC 3.672(e)), which replace the RA-010 notice where they exist",
      { text: "If a local rule requires it, make sure a copy of the RA-010 reaches the department hearing the conference.", cite: "CRC 3.672(g)(2)(D)" });
  } else {
    c.item(p5, "person", "Appear in person, familiar with the case and prepared to commit to your side's positions.", "CRC 3.722(c)");
  }
  if (initial && (a.ctype === "limited" || a.ctype === "collections")) c.verify(`Whether ${court.name} excuses attendance at initial case management conferences in limited civil cases by local rule (CRC 3.722(e)).`);

  /* ---- rows, verify ---- */
  c.row("Conference", fmt(conf), WHICH_TXT[a.which]);
  if (meetBy) c.row("Meet and confer by", fmt(meetBy.safe), "30 days before (CRC 3.724)");
  if (lodgeBy) c.row("File, serve and lodge by", fmt(lodgeBy.safe), `${lodgeDays} days before (${lodgeDetail.cite})`);
  c.row("File and serve CM-110 by", fmt(fileBy.safe), "15 days before (CRC 3.725(a))");
  if (remoteBy) c.row("Remote appearance notice by", fmt(remoteBy), "2 court days before (CRC 3.672(g)(2)(A))");
  if (initial && a.jury === "yes") c.row("Jury fee due", fmt(conf), "On or before the initial conference (CCP 631(c))");
  if (moved) c.verify(BACKWARD_NOTE);
  const earliest = [noticeBy, meetBy, lodgeBy, fileBy].filter(Boolean).map((d) => d.safe).reduce((m, d) => (d < m ? d : m));
  c.dates(court, earliest, conf);
  if (exemptCollections(a)) c.verify(`Whether ${court.name} still expects a CM-110 in a collections case where no responsive pleading has been filed (CRC 3.720(c); 3.740(c)(2)).`);

  const out = c.result();
  let n = 1;
  out.sections.forEach((s) => { s.title = s.title.replace(/^Phase \d+\./, () => `Phase ${n++}.`); });
  return out;
}
